import React, { useState, Fragment } from "react";
import { ChevronLeft, Wand2, Sparkles, Loader2, X, HelpCircle } from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { toast } from "sonner";
import { useHat } from "../../../../context/HatContext";
import { useUser } from "../../../../context/UserContext";
import { generateVAWithLLM } from "../../../../service/apiService";
import VAListView from "./VAListView";
import CreateVAForm from "./CreateVAForm";
import DuplicateVAForm from "./DuplicateVAForm";

// Define Hat/VA type
interface Hat {
  id?: string;
  hat_name: string;
  summary: string;
  initial_prompt: string;
  directions: string;
  is_public: boolean;
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean };
    include_knowledge_base: {
      enabled: boolean;
      knowledge_base_ids: string[];
    };
  };
  favorites?: {[key: string]: boolean};
  selected_icon?: File | null;
  updated_at?: string;
}

interface VirtualAssistantControllerProps {
  onClose?: () => void;
}

const VirtualAssistantController: React.FC<VirtualAssistantControllerProps> = ({ onClose }) => {
  const [selectedVA, setSelectedVA] = useState<Hat | null>(null);
  const { fetchHatsList } = useHat();
  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';

  const canEditVA = (): boolean => {
    // Create mode — user is creating their own VA, always allowed
    if (!selectedVA) return true;
    if (isAdmin) return true;
    const currentUserId = userId?.toLowerCase() || "";
    const isOwner = (selectedVA as any).user_id === currentUserId || (selectedVA as any).userId === currentUserId;
    if (isOwner) return true;
    const isManager = (selectedVA as any).managers && Array.isArray((selectedVA as any).managers)
      ? (selectedVA as any).managers.includes(currentUserId)
      : false;
    return isManager;
  };
  const [view, setView] = useState<"list" | "form" | "duplicate">("list");
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiInstructions, setAiInstructions] = useState("");
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiGeneratedFields, setAiGeneratedFields] = useState<Record<string, string> | null>(null);
  const [showAIHelp, setShowAIHelp] = useState(false);

  const handleCreateClick = () => {
    setSelectedVA(null);
    setAiGeneratedFields(null);
    setView("form");
  };

  const handleEditClick = (va: Hat) => {
    setSelectedVA(va);
    setAiGeneratedFields(null);
    setView("form");
  };

  const handleBackToList = () => {
    setView("list");
    setSelectedVA(null);
    setAiGeneratedFields(null);
  };

  const handleSaveComplete = () => {
    setView("list");
    setSelectedVA(null);
    setAiGeneratedFields(null);
    fetchHatsList();
  };

  const handleDuplicateClick = (va: Hat) => {
    setSelectedVA(va);
    setView("duplicate");
  };

  // Reference to get current form data from CreateVAForm
  const formDataRef = React.useRef<{ hat_name: string; summary: string; initial_prompt: string; directions: string } | null>(null);

  const handleAIGenerateSubmit = async () => {
    if (!aiInstructions.trim()) return;
    setIsAIGenerating(true);
    try {
      const currentVA = formDataRef.current || {
        hat_name: selectedVA?.hat_name || "",
        summary: selectedVA?.summary || "",
        initial_prompt: selectedVA?.initial_prompt || "",
        directions: selectedVA?.directions || "",
      };
      const result = await generateVAWithLLM(aiInstructions.trim(), currentVA);
      if (result?.fields) {
        setAiGeneratedFields(result.fields);
        toast.success("AI generated the changes! Review them in the form.");
        setShowAIModal(false);
        setAiInstructions("");
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to generate with AI. Please try again.");
    } finally {
      setIsAIGenerating(false);
    }
  };
  
  if (view === "form") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button + AI Button */}
      <div className="sticky top-0 z-20 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] -mx-4 px-4 py-3 w-[calc(100%+1.5rem)]">
        <div className="px-4 flex items-center justify-between">
          <button
            onClick={handleBackToList}
            className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back to Virtual Assistants
          </button>
          <button
            onClick={() => { if (canEditVA()) { setAiInstructions(""); setShowAIHelp(false); setShowAIModal(true); } }}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${canEditVA() ? "text-amber-600 bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300" : "text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed"}`}
            title={!canEditVA() ? "You don't have access to this VA" : "Modify with AI"}
          >
            <Wand2 className="h-4 w-4" />
            Modify with AI
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-4">
        <CreateVAForm
          vaData={selectedVA as any || undefined}
          onSave={handleSaveComplete}
          onCancel={handleBackToList}
          aiGeneratedFields={aiGeneratedFields}
          onAIFieldsApplied={() => setAiGeneratedFields(null)}
          formDataRef={formDataRef}
        />
      </div>

      {/* AI Generate/Modify Modal */}
      <Transition appear show={showAIModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => !isAIGenerating && setShowAIModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 bg-amber-50 rounded-full">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                          {selectedVA ? "Modify with AI" : "Generate with AI"}
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {selectedVA ? selectedVA.hat_name : "Describe the Virtual Assistant you want to create"}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => !isAIGenerating && setShowAIModal(false)}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                      disabled={isAIGenerating}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4">
                    {selectedVA
                      ? "Describe the changes you'd like to make. AI will update the form fields for you to review before saving."
                      : "Describe the Virtual Assistant you want to create. AI will fill in the name, summary, instructions, and more."}
                  </p>

                  {/* Instruction Input */}
                  <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <label htmlFor="ai-form-instructions" className="block text-sm font-medium text-gray-700">
                        Instructions
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowAIHelp(!showAIHelp)}
                        className="text-gray-400 hover:text-amber-500 transition-colors"
                        title="Click to see which fields AI can modify"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {showAIHelp && (
                      <div className="mb-2 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600">
                        <p className="font-medium text-gray-700 mb-1.5">AI can {selectedVA ? "modify" : "generate"} these fields:</p>
                        <ul className="space-y-1">
                          <li><span className="font-medium text-amber-600">Name</span> — Display name of the VA</li>
                          <li><span className="font-medium text-amber-600">Summary</span> — Brief description shown in the list</li>
                          <li><span className="font-medium text-amber-600">Initial Prompt</span> — First message sent to users</li>
                          <li><span className="font-medium text-amber-600">Instructions</span> — Behavior rules and guidelines</li>
                        </ul>
                      </div>
                    )}
                    <textarea
                      id="ai-form-instructions"
                      value={aiInstructions}
                      onChange={(e) => setAiInstructions(e.target.value)}
                      placeholder={selectedVA
                        ? "e.g., Make the tone more professional, add billing query handling..."
                        : "e.g., Create an HR assistant that helps with onboarding and leave policies..."}
                      className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none placeholder:text-gray-400"
                      disabled={isAIGenerating}
                      autoFocus
                    />
                  </div>

                  {/* AI Processing State */}
                  {isAIGenerating && (
                    <div className="mb-4 flex items-center gap-2 px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-100">
                      <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                      <span className="text-sm text-amber-700">
                        {selectedVA ? "AI is modifying your VA..." : "AI is generating your VA..."}
                      </span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none disabled:opacity-50"
                      onClick={() => { setShowAIModal(false); setAiInstructions(""); }}
                      disabled={isAIGenerating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAIGenerateSubmit}
                      disabled={isAIGenerating || !aiInstructions.trim()}
                    >
                      {isAIGenerating ? (
                        <div className="flex items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Generating...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <Wand2 className="h-4 w-4 mr-2" />
                          {selectedVA ? "Apply Changes" : "Generate"}
                        </div>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
  else if (view === "duplicate") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button */}
      <div className="sticky top-0 z-20 bg-white shadow-sm -mx-4 px-4 py-3 w-[calc(100%+1.5rem)]">
        <button 
          onClick={handleBackToList}
          className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to Virtual Assistants
        </button>
      </div>
      
      {/* Form Content */}
      <div className="p-4">
        <DuplicateVAForm 
          vaData={selectedVA || null}
          onClose={handleBackToList}
          onSuccess={handleSaveComplete}
        />
      </div>
    </div>
  );
}
  
  return (
    <VAListView 
      onCreateClick={handleCreateClick}
      onEditClick={handleEditClick as any}
      onDuplicateClick={handleDuplicateClick as any}
      onClose={onClose}  
    />
  );
};

export default VirtualAssistantController;